import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The reads for the Decisions screen (task 11.6; design 11, 12.2 Decisions row).
 *
 * Two operator-API operations back the screen, both role `viewer`, taken straight from the
 * generated `@oathera/contract/api` and never redefined here:
 *
 * - `GET /api/v1/decisions/summary` → {@link DecisionSummary}: allow/deny totals, the allow/deny/
 *   fail-closed over-time `series`, decision latency percentiles, the distinctly-highlighted
 *   fail-closed totals, and AIS↔Gateway consistency. This is the snapshot the whole screen rests on.
 * - `GET /api/v1/decisions/breakdown?by=reason|tier|operation|gateway` → {@link DecisionBreakdown}:
 *   one row per key with allow/deny/total, a `highlight` flag, and a `drill` {@link EventQuery} that
 *   names the exact `/events` filter for the events behind the row. The `by` dimension is a
 *   user-selectable control, so it is a query input: changing it re-queries this endpoint while the
 *   summary stays put.
 *
 * Both windows are resolved from the shared time-range exactly as the Overview/Service screens do,
 * so pausing freezes every query: in live mode "now" is bucketed to a 10s grid to keep the query key
 * stable between ticks; when frozen (paused or custom) `resolveWindow` returns the exact frozen
 * window and the key stops advancing (design 12.2, FR-13 AC2). Fetches go through `lib/api.ts`, so a
 * cold-core 503 surfaces as a `waking` {@link ApiError} and the shell shows "Waking the control
 * plane"; queries are retried per `query-client.ts`.
 */

/** The contract `DecisionSummary` response body. */
export type DecisionSummary = components["schemas"]["DecisionSummary"];
/** The contract `DecisionBreakdown` response body. */
export type DecisionBreakdown = components["schemas"]["DecisionBreakdown"];
/** The contract dimension the breakdown is grouped by (the `by` control's value set). */
export type DecisionBreakdownBy = components["schemas"]["DecisionBreakdownBy"];

/** The resolved time window a query ran for, so the screen can label the snapshot honestly. */
export interface DecisionsWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UseDecisionSummaryResult {
  readonly query: UseQueryResult<DecisionSummary, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: DecisionsWindow;
}

export interface UseDecisionBreakdownResult {
  readonly query: UseQueryResult<DecisionBreakdown, ApiError>;
  readonly window: DecisionsWindow;
}

/** Resolve the current window from the time-range (live buckets to 10s; frozen is exact). */
function useResolvedWindow(): DecisionsWindow {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  return { fromMs: resolved.fromMs, toMs: resolved.toMs };
}

/**
 * Fetch the decision summary for the current time window. The query key carries the window so a
 * frozen window never refetches and a live window advances on the 10s grid; `staleTime`/
 * `refetchInterval` match the other screens (live tolerates a little staleness, frozen never goes
 * stale).
 */
export function useDecisionSummary(): UseDecisionSummaryResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<DecisionSummary, ApiError>({
    queryKey: ["decisions-summary", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<DecisionSummary>("/decisions/summary", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch the breakdown grouped by `by` for the current window. `by` is part of the query key, so the
 * `by` control simply changes it and TanStack re-queries the endpoint (keeping each dimension's
 * result cached, so flipping back is instant). The window is resolved the same way as the summary so
 * both freeze together on pause.
 */
export function useDecisionBreakdown(by: DecisionBreakdownBy): UseDecisionBreakdownResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<DecisionBreakdown, ApiError>({
    queryKey: ["decisions-breakdown", by, window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<DecisionBreakdown>("/decisions/breakdown", {
        query: { by, from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous data visible while the next fetch runs: both across live window ticks (no
    // blink through loading on each ~10s refresh) and when flipping the `by` dimension (the table
    // never flashes empty). `keepPreviousData` is exactly this behaviour.
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
